import { isBaseDomainHost } from '@/lib/request-host';

/**
 * Absolute URL of a merchant's public donor portal. Portals live on a subdomain
 * (`correos.worldcoo.com`), NOT a path on the current host — so the portal host is derived from the
 * host the visitor is on, preserving domain, port and scheme across environments (`www.localhost:3001`
 * → `correos.localhost:3001`, `www.worldcoo.com` → `correos.worldcoo.com`).
 *
 * Derived, and not swapped blindly: this used to replace the FIRST label of whatever host the visitor
 * arrived on, which is only the site's own label when there is one. On the apex (`worldcoo.com`, which
 * serves the www site — see the router's `isMainDomain` rewrite) the first label is the domain itself,
 * so `nicequest` replaced `worldcoo` and every portal link pointed at `nicequest.com` — a domain we do
 * not own (TEC-3910). The apex and a bare dev host carry no site label, so the portal label goes in
 * FRONT of them; every other host (`www.`, another portal) has one to replace. `isBaseDomainHost`
 * is the shared answer to which of the two a host is — the NGO-area links derive their host the same
 * way, and were broken on the apex for the same reason.
 *
 * Shared by the merchant logo wheel and the homepage campaign carousel, which is why it is not a
 * private helper of either: they must agree on where a portal lives, and a second copy is how they
 * would stop agreeing.
 */
export function buildPortalHref(baseUrl: string, portalId: string): string {
	let url: URL;
	try {
		url = new URL(baseUrl);
	} catch {
		// No base URL yet (the client placeholder before the dehydrated context lands). A same-origin
		// path is wrong, but it is inert rather than pointing at somebody else's domain.
		return `/${portalId}`;
	}

	const labels = url.hostname.replace(/\.$/, '').split('.');
	const rest = isBaseDomainHost(url.hostname) ? labels : labels.slice(1);

	url.hostname = [portalId, ...rest].join('.');
	return url.origin;
}

/**
 * A campaign's page inside its merchant's portal. On a portal host the campaign lives at the root
 * (`correos.worldcoo.com/{slug}`) — there is no `/{portalId}` segment once the host has said which
 * portal it is.
 */
export function buildCampaignHref(baseUrl: string, portalId: string, slug: string): string {
	return `${buildPortalHref(baseUrl, portalId)}/${slug}`;
}
